// 60-second judges' demo: 3 static slides over 0-45s, then auto-launch a
// spectator match (AI vs AI) for 15s+ with AI dialogue/voice running live.
// Per v2-FINAL §3.1 the slides are static images + narration text, not a
// complex animation timeline -- keeps P7 inside its 1.5h budget.

import { PERSONALITIES, PERSONALITY_IDS } from '../ai/personalities';

export interface DemoEvents {
  onSlideEnd?: () => void;          // called when slide 3 finishes (~45s)
  onComplete: () => void;            // called when full demo ends (60s)
  onSkip?: () => void;               // user pressed skip
}

interface Slide {
  durationMs: number;
  build: () => HTMLElement;
}

export class DemoTour {
  root: HTMLDivElement;
  private current = 0;
  private timer: number | null = null;
  private events: DemoEvents;
  slides: Slide[];

  constructor(events: DemoEvents) {
    this.events = events;
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', inset: '0', zIndex: '120',
      background: 'radial-gradient(ellipse at center, #1f2645 0%, #06081a 80%)',
      color: '#fff', font: '14px/1.5 -apple-system, "PingFang SC", sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'opacity .4s', opacity: '0',
    } as CSSStyleDeclaration);
    this.slides = [
      { durationMs: 15000, build: () => this.buildIntro() },
      { durationMs: 15000, build: () => this.buildPersonalities() },
      { durationMs: 15000, build: () => this.buildArchitecture() },
    ];
    document.body.appendChild(this.root);

    // Skip button (always visible top-right).
    const skip = document.createElement('button');
    skip.textContent = '跳过 ✕';
    Object.assign(skip.style, {
      position: 'absolute', top: '14px', right: '14px',
      background: 'rgba(20,30,55,0.7)', color: '#dde',
      border: '1px solid rgba(180,200,255,0.3)', padding: '5px 12px',
      borderRadius: '14px', fontSize: '11px', letterSpacing: '1px', cursor: 'pointer',
    } as CSSStyleDeclaration);
    skip.addEventListener('click', () => this.skip());
    this.root.appendChild(skip);
  }

  start(): void {
    this.show(0);
    requestAnimationFrame(() => { this.root.style.opacity = '1'; });
  }

  private show(idx: number): void {
    this.current = idx;
    if (idx >= this.slides.length) {
      // Slides done -> overlay fades; spectator game starts.
      this.events.onSlideEnd?.();
      this.root.style.transition = 'opacity .8s';
      this.root.style.opacity = '0';
      setTimeout(() => {
        this.events.onComplete();
        this.destroy();
      }, 15000);
      return;
    }
    // Replace child container with new slide.
    const prev = this.root.querySelector('.demo-slide') as HTMLElement | null;
    if (prev) prev.remove();
    const el = this.slides[idx].build();
    el.classList.add('demo-slide');
    el.style.opacity = '0';
    el.style.transition = 'opacity .4s';
    this.root.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    this.timer = window.setTimeout(() => this.show(idx + 1), this.slides[idx].durationMs);
  }

  skip(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.events.onSkip?.();
    this.destroy();
    this.events.onComplete();
  }

  destroy(): void {
    if (this.timer != null) clearTimeout(this.timer);
    this.root.remove();
  }

  // ============== slide builders ==============

  private buildIntro(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.style.textAlign = 'center';
    wrap.style.maxWidth = '720px';
    wrap.innerHTML = `
      <div style="font-size:11px;letter-spacing:6px;color:#88aaff">SLIDE 1 / 3 · 0-15s</div>
      <div style="font-size:48px;font-weight:200;letter-spacing:10px;margin-top:12px;text-shadow:0 0 24px #88aaff80">PAPER WINGS</div>
      <div style="font-size:16px;color:#88aaff;letter-spacing:4px;margin-top:6px">AI DOGFIGHT · 纸飞机 AI 大战</div>
      <div style="margin-top:36px;font-size:18px;line-height:1.7">
        <div style="color:#fff">本作核心创新：</div>
        <div style="color:#ffd96b;font-size:24px;font-weight:300;margin-top:14px;letter-spacing:2px">
          用大语言模型让游戏 NPC 真正活起来
        </div>
      </div>
      <div style="margin-top:32px;color:#aac;font-size:13px;line-height:1.7;max-width:540px;margin-left:auto;margin-right:auto">
        传统 NPC 是状态机里的台词查找。<br/>
        Paper Wings 让每架 AI 纸飞机都有<b style="color:#fff">性格</b> ×
        <b style="color:#fff">临场情境</b> × <b style="color:#fff">玩家对话上下文</b><br/>
        = 永不重复的战场叙事。
      </div>
    `;
    return wrap;
  }

  private buildPersonalities(): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { maxWidth: '900px', textAlign: 'center', padding: '20px' } as CSSStyleDeclaration);

    let html = `
      <div style="font-size:11px;letter-spacing:6px;color:#88aaff">SLIDE 2 / 3 · 15-30s</div>
      <div style="font-size:24px;font-weight:300;letter-spacing:4px;margin:10px 0 18px">6 头 AI 性格 · 各有所长</div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;text-align:left">
    `;
    for (const id of PERSONALITY_IDS) {
      const p = PERSONALITIES[id];
      const sample = p.fallbackLines[0];
      html += `
        <div style="background:rgba(20,30,55,0.65);border:1px solid rgba(180,200,255,0.2);border-radius:8px;padding:12px">
          <div style="font-size:14px;font-weight:600;color:#88ccff">${p.displayName}</div>
          <div style="font-size:10px;color:#778;margin:4px 0 8px;letter-spacing:1px">${id.toUpperCase()}</div>
          <div style="font-size:11px;color:#aac;line-height:1.5">
            射程 <b style="color:#fff">${p.behavior.attackRange}m</b> ·
            激进 <b style="color:#fff">${(p.behavior.aggression * 100).toFixed(0)}%</b><br/>
            护队 <b style="color:#fff">${(p.behavior.teammateProtect * 100).toFixed(0)}%</b> ·
            撞击 <b style="color:#fff">${(p.behavior.recklessRam * 100).toFixed(0)}%</b>
          </div>
          <div style="margin-top:10px;padding:6px 8px;background:rgba(0,0,0,0.3);border-left:2px solid #ffaa44;font-size:12px;color:#ffe5b3">"${sample}"</div>
        </div>
      `;
    }
    html += `</div>
      <div style="margin-top:14px;font-size:12px;color:#778">数值差异 → 飞行行为差异。狙击手停 90m 外慢节奏，狂躁少年俯冲撞机。</div>`;
    wrap.innerHTML = html;
    return wrap;
  }

  private buildArchitecture(): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { maxWidth: '780px', textAlign: 'center', padding: '20px' } as CSSStyleDeclaration);

    wrap.innerHTML = `
      <div style="font-size:11px;letter-spacing:6px;color:#88aaff">SLIDE 3 / 3 · 30-45s</div>
      <div style="font-size:24px;font-weight:300;letter-spacing:4px;margin:10px 0 18px">技术架构 · LLM-NPC 系统</div>
      <pre style="text-align:left;background:rgba(0,0,0,0.4);border:1px solid #234;border-radius:8px;padding:16px;font:12px/1.6 ui-monospace,monospace;color:#bcd;letter-spacing:0.5px;overflow-x:auto">
┌────────────────────────────────────────────────────────────┐
│   浏览器 (Three.js + Web Speech + nipplejs)               │
│      │ /api/ai/say  和  /api/ai/personality              │
│      ▼                                                     │
│   <span style="color:#ffaa44">Cloudflare Pages Functions (TypeScript)</span>           │
│      · IP 限流 60/min                                      │
│      · CORS 严格 / Cache-Control: no-store                │
│      · 密钥仅在 wrangler secret                            │
│      │                                                     │
│      ▼                                                     │
│   <span style="color:#ff8a8a">DeepSeek-V4-flash (deepseek-chat 非思考模式)</span>      │
│      TTFB < 200ms · reasoning_tokens = 0                   │
└────────────────────────────────────────────────────────────┘</pre>
      <div style="text-align:left;background:rgba(20,30,55,0.5);border-radius:8px;padding:12px;margin-top:14px;font-size:13px;line-height:1.6">
        <b style="color:#88ccff">事件驱动对话</b>：opening / kill (30%) / death / ally_low (50%) / idle (30s) / player_radio<br/>
        <b style="color:#88ccff">PCG 临场背景</b>：每局开始为每架 AI 生成名字 / 心情 / 跨机恩怨 / 3 句口头禅<br/>
        <b style="color:#88ccff">语音电台</b>：Web Speech API + Web Audio 杂音滤镜，AI 听懂玩家中文指令并语义回应
      </div>
      <div style="margin-top:12px;font-size:11px;color:#778">下一秒：进入观战，亲眼看 6 性格 AI 如何嘴炮</div>
    `;
    return wrap;
  }
}
